import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  X,
  ChevronRight,
  ChevronLeft,
  Unplug,
  Link,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import {
  api,
  API_ORIGIN,
  ParsedImportResult,
  ImportConfirmResult,
  OAuthConnection,
  OAuthProvider,
} from '@/lib/api';

type Step = 'source' | 'upload' | 'preview' | 'options' | 'result';

const FORMAT_LABELS: Record<string, string> = {
  vcf: 'vCard',
  'google-csv': 'Google Contacts CSV',
  'outlook-csv': 'Outlook CSV',
  'generic-csv': 'CSV',
  'google-contacts': 'Google Contacts',
  'microsoft-contacts': 'Microsoft Contacts',
};

// SVG icons for Google and Microsoft (simple, recognizable)
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export function ContactImportDialog({
  open,
  onOpenChange,
  onImportComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}) {
  const [step, setStep] = useState<Step>('source');
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [fetchingProvider, setFetchingProvider] = useState<OAuthProvider | null>(null);
  const [parseResult, setParseResult] = useState<ParsedImportResult | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<Set<number>>(new Set());
  const [duplicateStrategy, setDuplicateStrategy] = useState<'skip' | 'overwrite' | 'create_new'>(
    'skip',
  );
  const [importResult, setImportResult] = useState<ImportConfirmResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [connections, setConnections] = useState<OAuthConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load connections when dialog opens
  useEffect(() => {
    if (open) {
      loadConnections();
    }
  }, [open]);

  // Listen for OAuth popup messages.
  //
  // The popup is served by the API host (`buildOAuthCallbackHtml` in
  // routes/arches/contact-book/contacts.ts) and posts to its opener here on the
  // frontend host. WITHOUT THE ORIGIN CHECK any page that can reach this window
  // could post a forged `oauth-complete`/`oauth-error` and render
  // attacker-controlled text inside the trusted UI — a phishing surface, since
  // the toast body below is taken straight from the message.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== API_ORIGIN) return;
      if (event.data?.type === 'oauth-complete') {
        loadConnections();
        toast({ title: `Connected to ${event.data.provider}` });
      } else if (event.data?.type === 'oauth-error') {
        toast({
          title: 'Authorization failed',
          description: event.data.error,
          variant: 'destructive',
        });
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const loadConnections = useCallback(async () => {
    setLoadingConnections(true);
    try {
      const result = await api.getImportConnections();
      setConnections(result.connections);
    } catch {
      // Silently fail — connections section just won't show status
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  const reset = useCallback(() => {
    setStep('source');
    setFile(null);
    setParsing(false);
    setImporting(false);
    setFetchingProvider(null);
    setParseResult(null);
    setSelectedContacts(new Set());
    setDuplicateStrategy('skip');
    setImportResult(null);
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setTimeout(reset, 200);
  }, [onOpenChange, reset]);

  const handleFileSelect = useCallback((selectedFile: File) => {
    const ext = selectedFile.name.toLowerCase().split('.').pop();
    if (!ext || !['vcf', 'vcard', 'csv'].includes(ext)) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a .vcf or .csv file.',
        variant: 'destructive',
      });
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Maximum file size is 10MB.',
        variant: 'destructive',
      });
      return;
    }
    setFile(selectedFile);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) handleFileSelect(droppedFile);
    },
    [handleFileSelect],
  );

  const handleParse = useCallback(async () => {
    if (!file) return;
    setParsing(true);
    try {
      const result = await api.parseContactImport(file);
      setParseResult(result);
      setSelectedContacts(new Set(result.contacts.map((_, i) => i)));
      setStep('preview');
    } catch (err) {
      toast({
        title: 'Failed to parse file',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setParsing(false);
    }
  }, [file]);

  const handleImport = useCallback(async () => {
    if (!parseResult) return;
    const contactsToImport = parseResult.contacts.filter((_, i) => selectedContacts.has(i));
    if (contactsToImport.length === 0) {
      toast({ title: 'No contacts selected', variant: 'destructive' });
      return;
    }

    setImporting(true);
    try {
      const result = await api.confirmContactImport({
        contacts: contactsToImport,
        duplicateStrategy,
      });
      setImportResult(result);
      setStep('result');
      onImportComplete();
    } catch (err) {
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  }, [parseResult, selectedContacts, duplicateStrategy, onImportComplete]);

  const handleOAuthConnect = useCallback(async (provider: OAuthProvider) => {
    try {
      const { authUrl } = await api.getOAuthAuthorizeUrl(provider);
      // Open popup
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(
        authUrl,
        `oauth-${provider}`,
        `width=${width},height=${height},left=${left},top=${top},popup=yes`,
      );
    } catch (err) {
      toast({
        title: 'Failed to start authorization',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, []);

  const handleProviderFetch = useCallback(async (provider: OAuthProvider) => {
    setFetchingProvider(provider);
    try {
      const result = await api.fetchProviderContacts(provider);
      setParseResult(result);
      setSelectedContacts(new Set(result.contacts.map((_, i) => i)));
      setStep('preview');
    } catch (err) {
      toast({
        title: `Failed to fetch contacts from ${provider}`,
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setFetchingProvider(null);
    }
  }, []);

  const handleDisconnect = useCallback(async (provider: OAuthProvider) => {
    try {
      await api.disconnectProvider(provider);
      setConnections((prev) => prev.filter((c) => c.provider !== provider));
      toast({ title: `Disconnected from ${provider}` });
    } catch (err) {
      toast({
        title: 'Failed to disconnect',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, []);

  const toggleContact = useCallback((index: number) => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (!parseResult) return;
    if (selectedContacts.size === parseResult.contacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(parseResult.contacts.map((_, i) => i)));
    }
  }, [parseResult, selectedContacts]);

  const getConnection = (provider: OAuthProvider) =>
    connections.find((c) => c.provider === provider && c.status === 'active');

  const stepDescription: Record<Step, string> = {
    source: 'Choose how to import your contacts.',
    upload: 'Upload a vCard (.vcf) or CSV file.',
    preview: 'Review the contacts to import.',
    options: 'Configure import options.',
    result: 'Import complete.',
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Contacts</DialogTitle>
          <DialogDescription>{stepDescription[step]}</DialogDescription>
        </DialogHeader>

        {/* Step 0: Source Selection */}
        {step === 'source' && (
          <div className="space-y-3">
            {/* Google Contacts */}
            <ProviderCard
              icon={<GoogleIcon className="h-6 w-6" />}
              name="Google Contacts"
              description="Import from your Google account"
              connection={getConnection('google')}
              loading={loadingConnections}
              fetching={fetchingProvider === 'google'}
              onConnect={() => handleOAuthConnect('google')}
              onFetch={() => handleProviderFetch('google')}
              onDisconnect={() => handleDisconnect('google')}
            />

            {/* Microsoft Contacts */}
            <ProviderCard
              icon={<MicrosoftIcon className="h-6 w-6" />}
              name="Microsoft Contacts"
              description="Import from Outlook or Microsoft 365"
              connection={getConnection('microsoft')}
              loading={loadingConnections}
              fetching={fetchingProvider === 'microsoft'}
              onConnect={() => handleOAuthConnect('microsoft')}
              onFetch={() => handleProviderFetch('microsoft')}
              onDisconnect={() => handleDisconnect('microsoft')}
            />

            {/* File Import */}
            <button
              className="w-full flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors text-left"
              onClick={() => setStep('upload')}
            >
              <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-muted">
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">File Import</p>
                <p className="text-xs text-muted-foreground">
                  Upload a vCard (.vcf) or CSV file — Apple Contacts, Google, Outlook, etc.
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        )}

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">Drop your file here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">
                Supports .vcf (vCard) and .csv (Google, Outlook) files up to 10MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".vcf,.vcard,.csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                  e.target.value = '';
                }}
              />
            </div>

            {file && (
              <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/50">
                <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && parseResult && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">
                  {FORMAT_LABELS[parseResult.format] || parseResult.format} —{' '}
                  {parseResult.totalParsed} contact{parseResult.totalParsed !== 1 ? 's' : ''} found
                </p>
                {(parseResult.warnings.length > 0 || parseResult.errors.length > 0) && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {parseResult.warnings.length > 0 && (
                      <span className="text-yellow-600">
                        {parseResult.warnings.length} warning
                        {parseResult.warnings.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {parseResult.warnings.length > 0 && parseResult.errors.length > 0 && ', '}
                    {parseResult.errors.length > 0 && (
                      <span className="text-destructive">
                        {parseResult.errors.length} error
                        {parseResult.errors.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={toggleAll}>
                {selectedContacts.size === parseResult.contacts.length
                  ? 'Deselect All'
                  : 'Select All'}
              </Button>
            </div>

            <div className="border rounded-md max-h-[40vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 w-8" />
                    <th className="p-2 text-left font-medium">Name</th>
                    <th className="p-2 text-left font-medium">Email</th>
                    <th className="p-2 text-left font-medium">Phone</th>
                    <th className="p-2 text-left font-medium">Company</th>
                  </tr>
                </thead>
                <tbody>
                  {parseResult.contacts.map((contact, idx) => (
                    <tr
                      key={idx}
                      className={`border-t hover:bg-muted/30 cursor-pointer ${
                        !selectedContacts.has(idx) ? 'opacity-50' : ''
                      }`}
                      onClick={() => toggleContact(idx)}
                    >
                      <td className="p-2">
                        <Checkbox
                          checked={selectedContacts.has(idx)}
                          onCheckedChange={() => toggleContact(idx)}
                        />
                      </td>
                      <td className="p-2 truncate max-w-[150px]">
                        {[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
                      </td>
                      <td className="p-2 truncate max-w-[180px] text-muted-foreground">
                        {contact.emails?.[0]?.value || '-'}
                      </td>
                      <td className="p-2 truncate max-w-[120px] text-muted-foreground">
                        {contact.phones?.[0]?.value || '-'}
                      </td>
                      <td className="p-2 truncate max-w-[120px] text-muted-foreground">
                        {contact.company || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {parseResult.warnings.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-yellow-600 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {parseResult.warnings.length} warning
                  {parseResult.warnings.length !== 1 ? 's' : ''}
                </summary>
                <ul className="mt-1 pl-5 list-disc text-xs text-muted-foreground space-y-0.5">
                  {parseResult.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </details>
            )}

            <p className="text-sm text-muted-foreground">
              {selectedContacts.size} of {parseResult.contacts.length} contacts selected
            </p>
          </div>
        )}

        {/* Step 3: Options */}
        {step === 'options' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Duplicate Handling</label>
              <Select
                value={duplicateStrategy}
                onValueChange={(v) => setDuplicateStrategy(v as typeof duplicateStrategy)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates (matching by email)</SelectItem>
                  <SelectItem value="overwrite">Overwrite existing contacts</SelectItem>
                  <SelectItem value="create_new">Always create new contacts</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Duplicates are detected by matching email addresses.
              </p>
            </div>

            <div className="p-3 rounded-md bg-muted/50 text-sm">
              <p className="font-medium">Ready to import</p>
              <p className="text-muted-foreground mt-1">
                {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} will be
                imported. All imported contacts will be tagged with{' '}
                <Badge variant="outline" className="text-xs">
                  imported
                </Badge>
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 'result' && importResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-md bg-green-50 dark:bg-green-950/20">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-medium">Import Complete</p>
                <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                  {importResult.imported > 0 && (
                    <p>
                      {importResult.imported} contact{importResult.imported !== 1 ? 's' : ''}{' '}
                      imported
                    </p>
                  )}
                  {importResult.updated > 0 && (
                    <p>
                      {importResult.updated} contact{importResult.updated !== 1 ? 's' : ''} updated
                    </p>
                  )}
                  {importResult.skipped > 0 && (
                    <p>
                      {importResult.skipped} duplicate{importResult.skipped !== 1 ? 's' : ''}{' '}
                      skipped
                    </p>
                  )}
                </div>
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-destructive flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {importResult.errors.length} error{importResult.errors.length !== 1 ? 's' : ''}
                </summary>
                <ul className="mt-1 pl-5 list-disc text-xs text-muted-foreground space-y-0.5">
                  {importResult.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'source' && (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}
          {step === 'upload' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setStep('source');
                  setFile(null);
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button onClick={handleParse} disabled={!file || parsing}>
                {parsing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {parsing ? 'Parsing...' : 'Parse File'}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setStep('source');
                  setFile(null);
                  setParseResult(null);
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button onClick={() => setStep('options')} disabled={selectedContacts.size === 0}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          )}
          {step === 'options' && (
            <>
              <Button variant="outline" onClick={() => setStep('preview')}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button onClick={handleImport} disabled={importing}>
                {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {importing
                  ? 'Importing...'
                  : `Import ${selectedContacts.size} Contact${selectedContacts.size !== 1 ? 's' : ''}`}
              </Button>
            </>
          )}
          {step === 'result' && <Button onClick={handleClose}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// PROVIDER CARD COMPONENT
// =============================================================================

function ProviderCard({
  icon,
  name,
  description,
  connection,
  loading,
  fetching,
  onConnect,
  onFetch,
  onDisconnect,
}: {
  icon: React.ReactNode;
  name: string;
  description: string;
  connection: OAuthConnection | undefined;
  loading: boolean;
  fetching: boolean;
  onConnect: () => void;
  onFetch: () => void;
  onDisconnect: () => void;
}) {
  const isConnected = !!connection;

  return (
    <div className="flex items-center gap-4 p-4 rounded-lg border">
      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-muted flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{name}</p>
        {isConnected ? (
          <p className="text-xs text-green-600 truncate">
            Connected{connection.providerAccountName ? ` as ${connection.providerAccountName}` : ''}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : isConnected ? (
          <>
            <Button size="sm" onClick={onFetch} disabled={fetching}>
              {fetching ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <ChevronRight className="h-4 w-4 mr-1" />
              )}
              {fetching ? 'Fetching...' : 'Import'}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={onDisconnect}
              title="Disconnect"
            >
              <Unplug className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" onClick={onConnect}>
            <Link className="h-4 w-4 mr-1" />
            Connect
          </Button>
        )}
      </div>
    </div>
  );
}
